import { RealtimeModes as modes } from "mobility-toolbox-js/api";
import { memo, useEffect, useRef } from "react";

import { addPeripherieLineIcons, addSelectedLine } from "../utils";
import useAppSelector from "../utils/hooks/useAppSelector";

/**
 * Highlights the selected line on the background map. Extracted from
 * LiveMap's onSelectedLineChange, which used to be called both directly
 * (selectedLine prop change) and from onModeChange (mode change).
 */
function SelectedLineChangeListener() {
  const config = useAppSelector((state) => state.config);
  const hideOthers = useAppSelector((state) => state.hideOthers);
  const mode = useAppSelector((state) => state.mode);
  const schematicBgLayer = useAppSelector((state) => state.schematicBgLayer);
  const selectedLine = useAppSelector((state) => state.selectedLine);
  const topographicBgLayer = useAppSelector(
    (state) => state.topographicBgLayer,
  );
  // Read via refs so the effect only needs to depend on mode/selectedLine,
  // the only values that trigger onSelectedLineChange in the original class.
  const configRef = useRef(config);
  const hideOthersRef = useRef(hideOthers);
  const schematicBgLayerRef = useRef(schematicBgLayer);
  const topographicBgLayerRef = useRef(topographicBgLayer);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    hideOthersRef.current = hideOthers;
  }, [hideOthers]);

  useEffect(() => {
    schematicBgLayerRef.current = schematicBgLayer;
  }, [schematicBgLayer]);

  useEffect(() => {
    topographicBgLayerRef.current = topographicBgLayer;
  }, [topographicBgLayer]);

  useEffect(() => {
    if (!selectedLine) {
      return;
    }

    const currentConfig = configRef.current;
    const currentHideOthers = hideOthersRef.current;
    const currentSchematicBgLayer = schematicBgLayerRef.current;
    const currentTopographicBgLayer = topographicBgLayerRef.current;

    const lineWidth = currentConfig[mode].paintLineWidthHighlight;
    const addGreyBg = currentConfig[mode].useHideOthersBg
      ? currentHideOthers
      : false;
    const bgLayer =
      mode === modes.SCHEMATIC
        ? currentSchematicBgLayer
        : currentTopographicBgLayer;

    if (bgLayer) {
      addSelectedLine(bgLayer, selectedLine, lineWidth, addGreyBg);

      if (
        currentConfig[mode].usePeripherieIcons &&
        mode === modes.SCHEMATIC &&
        currentSchematicBgLayer
      ) {
        addPeripherieLineIcons(
          currentSchematicBgLayer,
          addGreyBg ? selectedLine?.name : undefined,
          true,
        );
      }
    }
  }, [mode, selectedLine]);

  return null;
}

export default memo(SelectedLineChangeListener);
